import React, { useState, useContext, createContext } from 'react';
import { ApolloProvider, ApolloClient, InMemoryCache, ApolloLink, split } from '@apollo/client';
import { saveToken } from '../utils/auth';
import { useToast } from '@chakra-ui/react';
import { PATHS } from '../router/routes';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { getMainDefinition } from '@apollo/client/utilities';
import { WebSocketLink } from '@apollo/client/link/ws';
import { onError } from '@apollo/client/link/error';
import { SERVER_WS_URL } from '../utils/constants';
import { TokenAuth as LoginMutation } from '../graphql/mutations/TokenAuth';

const SERVER_API_URL = process.env.REACT_APP_SERVER_API_URL;

const authContext = createContext();

export function AuthProvider({ children }) {
    const auth = useProvideAuth();

    return (
        <authContext.Provider value={auth}>
            <ApolloProvider client={auth.createApolloClient()}>{children}</ApolloProvider>
        </authContext.Provider>
    );
}

export const useAuth = () => {
    return useContext(authContext);
};

function useProvideAuth() {
    const [loading, setLoading] = useState(false);
    const toast = useToast();
    const [authToken, setAuthToken] = useState(() => localStorage.getItem('token'));

    const isSignedIn = () => {
        if (authToken) {
            return true;
        } else {
            return false;
        }
    };

    const getAuthHeaders = () => {
        if (!authToken) return {};

        return {
            authorization: `JWT ${authToken}`,
        };
    };

    const httpUri = `${SERVER_API_URL}/django/graphql/`;

    const wsUri = `${SERVER_WS_URL}/django/graphql/subscription/`;

    const httpLink = createUploadLink({
        uri: httpUri,
        headers: getAuthHeaders(),
    });

    const wsLink = new WebSocketLink({
        uri: wsUri,
        options: {
            reconnect: true,
        },
    });

    const link = ApolloLink.from([
        onError(({ graphQLErrors, networkError, operation }) => {
            const context = operation.getContext();
            const incidentId = context.response?.headers.get('incident-id');

            if (graphQLErrors || networkError) {
                if (incidentId) {
                    sessionStorage.setItem('incidentId', incidentId);
                }
            }
        }),
        split(
            ({ query }) => {
                const definition = getMainDefinition(query);
                return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
            },
            wsLink,
            httpLink
        ),
    ]);

    const createApolloClient = () => {
        return new ApolloClient({
            link,
            cache: new InMemoryCache(),
        });
    };

    const verification = (token) => {
        localStorage.setItem('remindMe', true);
        setAuthToken(token);
        saveToken(token);
    };

    const signIn = async ({ email, password, isSignInPage = false }) => {
        setLoading(true);
        const client = createApolloClient();

        const result = await client.mutate({
            mutation: LoginMutation,
            variables: { email, password },
        });
        if (result?.data?.tokenAuth.success) {
            setAuthToken(result?.data?.tokenAuth?.token?.token);
            localStorage.setItem('email', email);
            saveToken(result?.data?.tokenAuth?.token?.token);
            window.location.href = PATHS.MY_AGENT;
        } else {
            if (isSignInPage) {
                setLoading(false);
                toast({
                    title: 'Wrong email or password',
                    status: 'error',
                    isClosable: true,
                });
            }
        }
    };

    const signOut = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('email');
        localStorage.removeItem('fromWhere');
        sessionStorage.clear();
        setAuthToken(null);
    };

    return {
        setAuthToken,
        isSignedIn,
        verification,
        setLoading,
        signIn,
        loading,
        signOut,
        createApolloClient,
        getAuthHeaders,
    };
}
